import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import {
  CallRecord,
  CallFilterResult,
  CallQueryResults,
} from "@/types/callFilters";
import api from "@/lib/api";
import { useOrganizationsStore } from "./organizationsStore";
import { persist } from "zustand/middleware";

interface CallsState {
  calls: CallRecord[];
  selectedCalls: CallRecord[];
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  pageSize: number;
  sortingState: { id: string; desc: boolean }[];
  lastFetchTime: number;
  totalCalls: number;
  totalPages: number;
  currentOrganizationId: string | null;
  filters: {
    search: string | null;
    call_status: string | null;
    strategy_type: string | null;
    was_successful: boolean | null;
    had_voicemail: boolean | null;
    days_ago: number | null;
  };
  // Smart Search State
  isSmartSearchMode: boolean;
  smartSearchQuery: string | null;
  smartSearchFilters: CallFilterResult | null;
  aggregations: {
    total_calls: number;
    by_status: Record<string, number>;
    by_strategy_type: Record<string, number>;
    avg_duration_seconds: number;
    total_duration_minutes: number;
  } | null;
  // Get calls for the currently selected organization
  fetchCallsForCurrentOrg: () => Promise<void>;
  // Fetch calls with advanced filtering
  fetchCallsAdvanced: (organizationId: string) => Promise<void>;
  // Fetch calls using smart search with v3 filters
  fetchCallsV3: (
    organizationId: string,
    v3Filters: CallFilterResult
  ) => Promise<void>;
  // Switch to smart search mode with v3 filters
  applySmartSearch: (
    query: string,
    v3Filters: CallFilterResult,
    results: CallQueryResults
  ) => void;
  // Clear smart search and return to regular filtering
  clearSmartSearch: () => void;
  // Clear calls (e.g., when logging out)
  clearCalls: () => void;
  // Set current page
  setCurrentPage: (page: number) => void;
  // Set sorting state
  setSortingState: (sorting: { id: string; desc: boolean }[]) => void;
  // Set page size
  setPageSize: (size: number) => void;
  // Set filters for server-side filtering
  setFilters: (filters: Partial<CallsState["filters"]>) => void;
  // Get total page count
  getPageCount: () => number;
  // Selection management
  toggleCallSelection: (call: CallRecord) => void;
  isCallSelected: (callId: string) => boolean;
  removeCallSelection: (callId: string) => void;
  clearSelections: () => void;
  // Reset state when organization changes
  resetPaginationState: () => void;
}

export const useCallsStore = create<CallsState>()(
  persist(
    immer<CallsState>((set, get) => ({
      calls: [],
      selectedCalls: [],
      isLoading: false,
      error: null,
      currentPage: 0,
      pageSize: 20,
      sortingState: [],
      lastFetchTime: 0,
      totalCalls: 0,
      totalPages: 0,
      currentOrganizationId: null,
      filters: {
        search: null,
        call_status: null,
        strategy_type: null,
        was_successful: null,
        had_voicemail: null,
        days_ago: null,
      },
      // Smart Search State
      isSmartSearchMode: false,
      smartSearchQuery: null,
      smartSearchFilters: null,
      aggregations: null,

      fetchCallsAdvanced: async (organizationId: string) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
          state.currentOrganizationId = organizationId;
        });

        try {
          // Convert sorting state to format expected by API
          const sortingState = get().sortingState;
          let sortBy = "created_at"; // default
          let sortOrder = "desc"; // default

          if (sortingState.length > 0) {
            sortBy = sortingState.map((sort) => sort.id).join(",");
            sortOrder = sortingState
              .map((sort) => (sort.desc ? "desc" : "asc"))
              .join(",");
          }

          const filters = get().filters;
          const currentPage = get().currentPage;
          const pageSize = get().pageSize;

          // Map regular filters to API parameters
          const apiParams: CallFilterResult & {
            limit?: number;
            offset?: number;
            sort_by?: string;
            sort_order?: string;
          } = {
            limit: pageSize,
            offset: currentPage * pageSize,
            sort_by: sortBy,
            sort_order: sortOrder,
          };

          // Map filters
          if (filters.search) {
            apiParams.patient_name = filters.search;
          }
          if (filters.call_status) {
            apiParams.call_statuses = filters.call_status;
          }
          if (filters.strategy_type) {
            apiParams.strategy_types = filters.strategy_type;
          }
          if (filters.was_successful !== null) {
            apiParams.was_successful = filters.was_successful;
          }
          if (filters.had_voicemail !== null) {
            apiParams.had_voicemail = filters.had_voicemail;
          }
          if (filters.days_ago !== null) {
            apiParams.days_ago = filters.days_ago;
          }

          const response = await api.queryCallsV3(organizationId, apiParams);

          set((state) => {
            state.calls = response.calls;
            state.totalCalls = response.pagination.total;
            state.totalPages = Math.ceil(
              response.pagination.total / response.pagination.limit
            );
            state.aggregations = response.aggregations;

            // Ensure current page is valid for the new data set
            const maxPageIndex = Math.max(0, state.totalPages - 1);
            if (state.currentPage > maxPageIndex) {
              console.log(
                `Current page ${state.currentPage} exceeds max page ${maxPageIndex}, resetting to 0`
              );
              state.currentPage = 0;
            }

            state.isLoading = false;
            state.lastFetchTime = Date.now();
            state.currentOrganizationId = organizationId;
          });
        } catch (error) {
          console.error("Failed to fetch calls:", error);
          set((state) => {
            state.error = "Failed to fetch calls";
            state.isLoading = false;
          });
        }
      },

      fetchCallsForCurrentOrg: async () => {
        const selectedOrgId =
          useOrganizationsStore.getState().selectedOrganizationId;

        if (!selectedOrgId) {
          set((state) => {
            state.error = "No organization selected";
          });
          return;
        }

        await get().fetchCallsAdvanced(selectedOrgId);
      },

      fetchCallsV3: async (
        organizationId: string,
        v3Filters: CallFilterResult
      ) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
          state.currentOrganizationId = organizationId;
        });

        try {
          const currentPage = get().currentPage;
          const pageSize = get().pageSize;

          const response = await api.queryCallsV3(organizationId, {
            ...v3Filters,
            limit: pageSize,
            offset: currentPage * pageSize,
          });

          set((state) => {
            state.calls = response.calls;
            state.totalCalls = response.pagination.total;
            state.totalPages = Math.ceil(
              response.pagination.total / response.pagination.limit
            );
            state.aggregations = response.aggregations;

            // Ensure current page is valid for the new data set
            const maxPageIndex = Math.max(0, state.totalPages - 1);
            if (state.currentPage > maxPageIndex) {
              console.log(
                `Current page ${state.currentPage} exceeds max page ${maxPageIndex}, resetting to 0`
              );
              state.currentPage = 0;
            }

            state.isLoading = false;
            state.lastFetchTime = Date.now();
            state.currentOrganizationId = organizationId;
          });
        } catch (error) {
          console.error("Failed to fetch calls with v3:", error);
          set((state) => {
            state.error = "Failed to fetch calls";
            state.isLoading = false;
          });
        }
      },

      applySmartSearch: (
        query: string,
        v3Filters: CallFilterResult,
        results: CallQueryResults
      ) => {
        set((state) => {
          state.isSmartSearchMode = true;
          state.smartSearchQuery = query;
          state.smartSearchFilters = v3Filters;
          state.calls = results.calls;
          state.totalCalls = results.pagination.total;
          state.totalPages = Math.ceil(
            results.pagination.total / results.pagination.limit
          );
          state.aggregations = results.aggregations;
          // Clear regular filters when smart search is applied
          state.filters = {
            search: null,
            call_status: null,
            strategy_type: null,
            was_successful: null,
            had_voicemail: null,
            days_ago: null,
          };
          // Reset to first page
          state.currentPage = 0;
        });
      },

      clearSmartSearch: () => {
        set((state) => {
          state.isSmartSearchMode = false;
          state.smartSearchQuery = null;
          state.smartSearchFilters = null;
          state.aggregations = null;
        });
      },

      clearCalls: () => {
        set((state) => {
          state.calls = [];
          state.error = null;
        });
      },

      setCurrentPage: (page) => {
        set((state) => {
          state.currentPage = page;
        });
      },

      setSortingState: (sorting) => {
        set((state) => {
          state.sortingState = sorting;
        });
      },

      setPageSize: (size) => {
        set((state) => {
          state.pageSize = size;
        });
      },

      setFilters: (newFilters) => {
        set((state) => {
          state.filters = {
            ...state.filters,
            ...newFilters,
          };
          // Reset to first page when filters change
          state.currentPage = 0;
          // Clear smart search when regular filters are applied
          state.isSmartSearchMode = false;
          state.smartSearchQuery = null;
          state.smartSearchFilters = null;
          state.aggregations = null;
        });
      },

      getPageCount: () => {
        const { calls, pageSize } = get();
        return Math.ceil(calls.length / pageSize);
      },

      // Selection management functions
      toggleCallSelection: (call) => {
        set((state) => {
          const isSelected = state.selectedCalls.some((c) => c.id === call.id);

          if (isSelected) {
            // Remove from selection
            state.selectedCalls = state.selectedCalls.filter(
              (c) => c.id !== call.id
            );
          } else {
            // Add to selection
            state.selectedCalls.push(call);
          }
        });
      },

      isCallSelected: (callId) => {
        return get().selectedCalls.some((c) => c.id === callId);
      },

      removeCallSelection: (callId) => {
        set((state) => {
          state.selectedCalls = state.selectedCalls.filter(
            (c) => c.id !== callId
          );
        });
      },

      clearSelections: () => {
        set((state) => {
          state.selectedCalls = [];
        });
      },

      // Reset state when organization changes
      resetPaginationState: () => {
        set((state) => {
          state.calls = [];
          state.selectedCalls = [];
          state.isLoading = false;
          state.error = null;
          state.currentPage = 0;
          state.pageSize = 10;
          state.sortingState = [];
          state.lastFetchTime = 0;
          state.totalCalls = 0;
          state.totalPages = 0;
          state.currentOrganizationId = null;
          state.filters = {
            search: null,
            call_status: null,
            strategy_type: null,
            was_successful: null,
            had_voicemail: null,
            days_ago: null,
          };
          state.isSmartSearchMode = false;
          state.smartSearchQuery = null;
          state.smartSearchFilters = null;
          state.aggregations = null;
        });
      },
    })),
    {
      name: "calls-storage", // Name for the localStorage item
      partialize: (state) => ({
        calls: state.calls,
        selectedCalls: state.selectedCalls,
        currentPage: state.currentPage,
        sortingState: state.sortingState,
        lastFetchTime: state.lastFetchTime,
        currentOrganizationId: state.currentOrganizationId,
        pageSize: state.pageSize,
        filters: state.filters,
        isSmartSearchMode: state.isSmartSearchMode,
        smartSearchQuery: state.smartSearchQuery,
        smartSearchFilters: state.smartSearchFilters,
        aggregations: state.aggregations,
      }),
    }
  )
);
